import { useEffect, useState } from 'react';
import type { FormEvent, PointerEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchMe, signup } from '../lib/api';
import { useEmailField } from '../hooks/useEmailValidation';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { connectionErrorMessage, getErrorStatus } from './authErrors';
import { GithubSignInButton } from './GithubSignInButton';
import { GoogleSignInButton } from './GoogleSignInButton';

const PASSWORD_MIN_LEN = 12;
const DISPLAY_NAME_MAX_LEN = 255;
const SIGNUP_EMAIL_ERROR_ID = 'signup-email-error';
const SIGNUP_PASSWORD_ERROR_ID = 'signup-password-hint';
const SIGNUP_PASSWORD_STRENGTH_ID = 'signup-password-strength';
const SIGNUP_CONFIRM_PASSWORD_ERROR_ID = 'signup-confirm-password-error';
const SIGNUP_DISPLAY_HELP_ID = 'signup-display-help';
const SESSION_DETECT_ATTEMPTS = 4;
const SESSION_DETECT_DELAY_MS = 150;
/** Where a brand-new account opens (T-0175). The canvas IS the first screen. */
const ORG_CANVAS = '/org';

function preserveSwitchClick(e: PointerEvent<HTMLButtonElement>) {
  e.preventDefault();
}

function passwordStrength(pw: string): 'weak' | 'fair' | 'strong' {
  let score = 0;
  if (pw.length >= PASSWORD_MIN_LEN) score++;
  if (pw.length >= 20) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return 'weak';
  if (score <= 3) return 'fair';
  return 'strong';
}

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

export function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const queryClient = useQueryClient();
  const emailField = useEmailField('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [postSignupState, setPostSignupState] = useState<
    'idle' | 'checking-session' | 'email-fallback'
  >('idle');

  const mutation = useMutation({
    mutationFn: signup,
  });

  useEffect(() => {
    if (!mutation.isSuccess) return undefined;

    let cancelled = false;
    setPostSignupState('checking-session');

    async function detectSession() {
      void queryClient.invalidateQueries({
        queryKey: ['me'],
        refetchType: 'none',
      });

      for (let attempt = 0; attempt < SESSION_DETECT_ATTEMPTS; attempt += 1) {
        if (cancelled) return;
        if (attempt > 0) {
          await delay(SESSION_DETECT_DELAY_MS);
        }

        try {
          await fetchMe();
          // T-0175 — the NEXT screen after signup is the canvas. It used to be the
          // `/onboarding` "Start your operator console" fork, which asked a brand-new
          // account to choose between two nouns it had no way to have opinions about.
          // Straight to `/org`, not to `/`: the bare root only *redirects* to the canvas
          // (App.tsx `shouldLandOnOrg`), so routing through it would flash the messaging
          // shell and cost a second navigation to reach the same place.
          if (!cancelled && window.location.pathname !== ORG_CANVAS) {
            window.location.href = ORG_CANVAS;
          }
          return;
        } catch {
          // Keep polling briefly: the Set-Cookie from signup can arrive just
          // before React Query's signed-out /api/me cache has refreshed.
        }
      }

      if (!cancelled) {
        setPostSignupState('email-fallback');
      }
    }

    void detectSession();

    return () => {
      cancelled = true;
    };
  }, [mutation.isSuccess, queryClient]);

  useDocumentTitle(
    mutation.isSuccess && postSignupState === 'email-fallback'
      ? 'Check your email'
      : mutation.isSuccess
        ? 'Finishing setup'
        : 'Create account',
  );

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    emailField.onBlur();
    setPasswordTouched(true);
    setConfirmPasswordTouched(true);
    if (!isFormValid(emailField.isValid, password, confirmPassword, displayName)) {
      return;
    }
    mutation.mutate({
      email: emailField.value.trim(),
      password,
      display_name: displayName.trim() || undefined,
    });
  }

  const error = mutation.error;
  const passwordTooShort =
    password.length > 0 && password.length < PASSWORD_MIN_LEN;
  const passwordError =
    passwordTouched && password.length === 0
      ? 'Create a password.'
      : passwordTooShort
        ? `Password must be at least ${PASSWORD_MIN_LEN} characters.`
        : null;
  const strength =
    password.length >= PASSWORD_MIN_LEN ? passwordStrength(password) : null;
  const passwordsMismatch =
    confirmPassword.length > 0 && confirmPassword !== password;
  const confirmPasswordError =
    confirmPasswordTouched && confirmPassword.length === 0
      ? 'Confirm your password.'
      : passwordsMismatch
        ? 'Passwords do not match.'
        : null;
  const emailError =
    emailField.error ??
    (emailField.touched && emailField.value.trim().length === 0
      ? 'Enter your email address.'
      : null);
  const canSubmit = isFormValid(
    emailField.isValid,
    password,
    confirmPassword,
    displayName,
  );
  const passwordDescriptionIds = [
    passwordError ? SIGNUP_PASSWORD_ERROR_ID : null,
    strength ? SIGNUP_PASSWORD_STRENGTH_ID : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  if (mutation.isSuccess && postSignupState !== 'email-fallback') {
    return (
      <div className="login-form" role="status" aria-live="polite">
        <h2>Finishing setup</h2>
        <p className="login-form-subtitle">
          Checking your new session before opening your canvas.
        </p>
        <p className="login-form-cli-hint">
          This usually takes a moment after account creation.
        </p>
      </div>
    );
  }

  if (mutation.isSuccess) {
    return (
      <div className="login-form" role="status" aria-live="polite">
        <h2>Check your email</h2>
        <p className="login-form-subtitle">
          We sent a verification link to <strong>{emailField.value}</strong>. Click it to
          finish setting up your account.
        </p>
        <p className="login-form-cli-hint">
          Didn't get it? Check spam — you can also sign in below to continue and
          verify later.
        </p>
        <button
          type="button"
          className="login-form-submit"
          onClick={onSwitchToLogin}
        >
          Back to Sign in
        </button>
      </div>
    );
  }

  return (
    <form
      className="login-form"
      onSubmit={handleSubmit}
      aria-label="Create account"
      noValidate
    >
      <h2>Create account</h2>
      <p className="login-form-subtitle">
        Tizok — the operator console for your AI coworkers.
      </p>

      <div className="oauth-section">
        <GithubSignInButton label="Sign up with GitHub" />
        <GoogleSignInButton label="Sign up with Google" />
        <div className="oauth-divider">or</div>
      </div>

      {error && (
        <div className="login-form-error" role="alert" aria-live="assertive">
          {signupErrorMessage(error)}
        </div>
      )}

      <label className="login-form-field">
        <span>Email</span>
        <input
          autoFocus
          type="email"
          name="email"
          autoComplete="email"
          required
          value={emailField.value}
          onChange={(e) => {
            emailField.setValue(e.target.value);
            if (mutation.isError) mutation.reset();
          }}
          onBlur={emailField.onBlur}
          disabled={mutation.isPending}
          aria-invalid={emailError ? true : undefined}
          aria-describedby={emailError ? SIGNUP_EMAIL_ERROR_ID : undefined}
        />
        {emailError && (
          <span
            id={SIGNUP_EMAIL_ERROR_ID}
            className="login-form-field-error"
            aria-live="polite"
          >
            {emailError}
          </span>
        )}
      </label>

      <div className="login-form-field">
        <span>
          <label htmlFor="signup-password">Password</label>{' '}
          <span className="login-form-hint-inline">(min 12 chars)</span>
        </span>
        <div className="login-form-password-wrapper">
          <input
            id="signup-password"
            type={passwordVisible ? 'text' : 'password'}
            name="password"
            autoComplete="new-password"
            required
            minLength={PASSWORD_MIN_LEN}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (mutation.isError) mutation.reset();
            }}
            onBlur={() => setPasswordTouched(true)}
            disabled={mutation.isPending}
            aria-invalid={passwordError ? true : undefined}
            aria-describedby={passwordDescriptionIds}
          />
          <button
            type="button"
            className="login-form-password-toggle"
            aria-label={passwordVisible ? 'Hide password' : 'Show password'}
            aria-pressed={passwordVisible}
            onPointerDown={preserveSwitchClick}
            onClick={() => setPasswordVisible((v) => !v)}
            disabled={mutation.isPending}
          >
            {passwordVisible ? 'Hide' : 'Show'}
          </button>
        </div>
        {passwordError && (
          <span
            id={SIGNUP_PASSWORD_ERROR_ID}
            className="login-form-field-error"
            aria-live="polite"
          >
            {passwordError}
          </span>
        )}
        {strength && (
          <div
            id={SIGNUP_PASSWORD_STRENGTH_ID}
            className={`login-form-strength login-form-strength--${strength}`}
            aria-label={`Password strength: ${strength}`}
          >
            <div className="login-form-strength-bar" aria-hidden="true" />
            <span className="login-form-strength-label">{strength}</span>
          </div>
        )}
      </div>

      <label className="login-form-field">
        <span>Confirm password</span>
        <input
          type={passwordVisible ? 'text' : 'password'}
          name="confirm_password"
          autoComplete="new-password"
          required
          minLength={PASSWORD_MIN_LEN}
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (mutation.isError) mutation.reset();
          }}
          onBlur={() => setConfirmPasswordTouched(true)}
          disabled={mutation.isPending}
          aria-invalid={confirmPasswordError ? true : undefined}
          aria-describedby={
            confirmPasswordError ? SIGNUP_CONFIRM_PASSWORD_ERROR_ID : undefined
          }
        />
        {confirmPasswordError && (
          <span
            id={SIGNUP_CONFIRM_PASSWORD_ERROR_ID}
            className="login-form-field-error"
          >
            {confirmPasswordError}
          </span>
        )}
      </label>

      <label className="login-form-field">
        <span>
          Display name <span className="login-form-hint-inline">(optional)</span>
        </span>
        <input
          type="text"
          name="display_name"
          autoComplete="nickname"
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value);
            if (mutation.isError) mutation.reset();
          }}
          disabled={mutation.isPending}
          maxLength={DISPLAY_NAME_MAX_LEN}
          placeholder="Defaults to email local-part"
          aria-describedby={SIGNUP_DISPLAY_HELP_ID}
        />
        <span id={SIGNUP_DISPLAY_HELP_ID} className="login-form-field-help">
          Optional. Use {DISPLAY_NAME_MAX_LEN} characters or fewer.
        </span>
      </label>

      <button
        type="submit"
        className="login-form-submit"
        disabled={mutation.isPending || !canSubmit}
      >
        {mutation.isPending && (
          <span className="button-spinner" aria-hidden="true" />
        )}
        {mutation.isPending ? 'Creating account…' : 'Create account'}
      </button>

      <div className="login-form-links">
        <span>Already have an account?</span>
        <button
          type="button"
          className="login-form-link-button"
          onPointerDown={preserveSwitchClick}
          onClick={onSwitchToLogin}
        >
          Sign in
        </button>
      </div>
    </form>
  );
}

function isFormValid(
  emailIsValid: boolean,
  password: string,
  confirmPassword: string,
  displayName: string,
): boolean {
  return (
    emailIsValid &&
    password.length >= PASSWORD_MIN_LEN &&
    confirmPassword === password &&
    displayName.trim().length <= DISPLAY_NAME_MAX_LEN
  );
}

function signupErrorMessage(error: unknown): string {
  const status = getErrorStatus(error);
  if (status === 409) {
    return 'An account already exists for this email. Sign in instead, or reset your password.';
  }
  if (status === 422) {
    return 'Use a valid email address and a password with at least 12 characters.';
  }
  if (status === 429) {
    return 'Too many signup attempts. Wait a few minutes, then try again.';
  }
  if (status === null) return connectionErrorMessage();
  if (status >= 500) {
    return "We couldn't create your account because the server had a problem. Try again in a minute.";
  }
  return "We couldn't create your account. Try again in a minute.";
}
